import type {
  Assessment,
  AssessmentEntryMode,
  AssessmentType,
} from "@/types/assessment";

export type StudentCardStatus = "assessment" | "report" | "new";

export type StudentRecommendedAction =
  | { type: "continue_assessment"; assessment: Assessment }
  | { type: "view_report"; assessment: Assessment }
  | { type: "start_assessment"; assessment: null };

function timeValue(value: string | undefined): number {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function newestFirst(
  left: Assessment,
  right: Assessment,
  dateFor: (assessment: Assessment) => string | undefined,
): number {
  const byDate = timeValue(dateFor(right)) - timeValue(dateFor(left));
  return byDate || left.id.localeCompare(right.id);
}

export function assessmentsForStudent(
  assessments: readonly Assessment[],
  studentId: string,
): Assessment[] {
  return assessments.filter((assessment) => assessment.studentId === studentId);
}

export function inProgressAssessmentsForStudent(
  assessments: readonly Assessment[],
  studentId: string,
): Assessment[] {
  return assessmentsForStudent(assessments, studentId)
    .filter((assessment) => assessment.status === "in_progress")
    .sort((left, right) => newestFirst(left, right, (assessment) => assessment.startedAt));
}

export function completedAssessmentsForStudent(
  assessments: readonly Assessment[],
  studentId: string,
): Assessment[] {
  return assessmentsForStudent(assessments, studentId)
    .filter((assessment) => assessment.status === "completed")
    .sort((left, right) =>
      newestFirst(left, right, (assessment) => assessment.submittedAt ?? assessment.startedAt),
    );
}

export function latestInProgressAssessment(
  assessments: readonly Assessment[],
  studentId: string,
): Assessment | undefined {
  return inProgressAssessmentsForStudent(assessments, studentId)[0];
}

export function latestCompletedAssessment(
  assessments: readonly Assessment[],
  studentId: string,
): Assessment | undefined {
  return completedAssessmentsForStudent(assessments, studentId)[0];
}

export function studentRecommendedAction(
  assessments: readonly Assessment[],
  studentId: string,
): StudentRecommendedAction {
  const active = latestInProgressAssessment(assessments, studentId);
  if (active) {
    return { type: "continue_assessment", assessment: active };
  }

  const completed = latestCompletedAssessment(assessments, studentId);
  if (completed) {
    return { type: "view_report", assessment: completed };
  }

  return { type: "start_assessment", assessment: null };
}

export function studentCardStatus(
  assessments: readonly Assessment[],
  studentId: string,
): StudentCardStatus {
  const action = studentRecommendedAction(assessments, studentId);
  if (action.type === "continue_assessment") {
    return "assessment";
  }
  if (action.type === "view_report") {
    return "report";
  }
  return "new";
}

export function assessmentBelongsToStudent(
  assessment: Assessment | undefined,
  studentId: string,
  _expectedType?: AssessmentType,
): boolean {
  void _expectedType;
  return Boolean(assessment && assessment.studentId === studentId);
}

export function assessmentEntryMode(assessment: Assessment): AssessmentEntryMode {
  return assessment.recordKind === "comprehensive"
    ? assessment.lastEntryMode
    : assessment.type;
}

export function assessmentRecordLabel(): string {
  return "综合评估";
}

export function entryModeLabel(mode: AssessmentEntryMode): string {
  return mode === "scale" ? "完整量表评估" : "活动评估";
}

export function isReportEligible(assessment: Assessment): boolean {
  return assessment.status === "completed" && assessment.integrityStatus === "verified";
}

export function studentHasReport(
  assessments: readonly Assessment[],
  studentId: string,
): boolean {
  return assessmentsForStudent(assessments, studentId).some(isReportEligible);
}

const childPath = (studentId: string) => `/children/${encodeURIComponent(studentId)}`;

export const studentWorkspacePaths = {
  children: "/children",
  scaleLibrary: "/scale",
  overview: (studentId: string) => `${childPath(studentId)}/overview`,
  assessments: (studentId: string) => `${childPath(studentId)}/assessments`,
  assessment: (studentId: string, type: AssessmentType, assessmentId: string) =>
    `${childPath(studentId)}/assessments/${type === "scale" ? "scale" : "activity"}?assessmentId=${encodeURIComponent(assessmentId)}`,
  reports: (studentId: string, assessmentId?: string) =>
    `${childPath(studentId)}/reports${assessmentId ? `?assessmentId=${encodeURIComponent(assessmentId)}` : ""}`,
  reportDetail: (studentId: string, assessmentId: string) =>
    `${childPath(studentId)}/reports/assessment/${encodeURIComponent(assessmentId)}`,
  reportAnalytics: (studentId: string, assessmentId?: string) =>
    `${childPath(studentId)}/reports/analytics${assessmentId ? `?assessmentId=${encodeURIComponent(assessmentId)}` : ""}`,
  reportCompare: (studentId: string, assessmentIds: readonly string[] = []) =>
    `${childPath(studentId)}/reports/compare${assessmentIds.length ? `?assessments=${encodeURIComponent(assessmentIds.join(","))}` : ""}`,
  parentReport: (studentId: string, assessmentId?: string) =>
    `${childPath(studentId)}/parent${assessmentId ? `?assessmentId=${encodeURIComponent(assessmentId)}` : ""}`,
  iep: (studentId: string, assessmentId?: string) =>
    `${childPath(studentId)}/iep${assessmentId ? `?assessmentId=${encodeURIComponent(assessmentId)}` : ""}`,
} as const;
